import { useState, useMemo, useCallback, useEffect } from "react";
import { api, DAYS, DAY_LABELS, MEALS, MEAL_LABELS, getWeekStart } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarPlus, ChefHat, CakeSlice, Check, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_BAKING_CATEGORIES = ["Bakst", "Dessert", "Kake", "Kjeks", "Brød", "Annet"];

/**
 * Reusable dialog to add a recipe to the weekly meal plan OR the baking plan.
 * Works for both Boat edition (uses shift) and Home Edition (uses shift "1").
 *
 * Props:
 *   - open: boolean
 *   - onOpenChange: (boolean) => void
 *   - recipe: { id, title, meal_type?, is_baking?, category? }
 */
export default function AddToPlanDialog({ open, onOpenChange, recipe, bulkRecipeIds, allRecipes, onAfterSave }) {
  const { activeBoat } = useAuth();
  const isHousehold = activeBoat?.kind === "household";
  const bakingCategories = (activeBoat?.baking_categories && activeBoat.baking_categories.length > 0)
    ? activeBoat.baking_categories
    : DEFAULT_BAKING_CATEGORIES;

  const defaultMealType = useMemo(() => {
    if (!recipe) return "dinner";
    if (recipe.is_baking) return "baking";
    const mt = (recipe.meal_type || "").toLowerCase();
    if (["breakfast", "lunch", "dinner", "dessert"].includes(mt)) return mt;
    return "dinner";
  }, [recipe]);

  const [mealType, setMealType] = useState(defaultMealType);
  const [day, setDay] = useState("monday");
  const [bakingCategory, setBakingCategory] = useState(recipe?.category || "Bakst");
  const [weekStart, setWeekStart] = useState(getWeekStart());
  const [shift, setShift] = useState(() => {
    try {
      const s = localStorage.getItem("pb-active-shift");
      if (s === "1" || s === "2") return s;
    } catch { /* ignore */ }
    return "1";
  });
  const [saving, setSaving] = useState(false);

  // Re-sync defaults when dialog opens with a different recipe
  useEffect(() => {
    if (open) {
      setMealType(defaultMealType);
      setDay("monday");
      setBakingCategory(recipe?.category || "Bakst");
      setWeekStart(getWeekStart());
      // Pick up the latest active shift from localStorage every time the
      // dialog is opened — keeps in-sync with WeeklyPlan toggle.
      try {
        const s = localStorage.getItem("pb-active-shift");
        if (s === "1" || s === "2") setShift(s);
      } catch { /* ignore */ }
    }
  }, [open, defaultMealType, recipe]);

  const shiftWeek = (dir) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + dir * 7);
    setWeekStart(d.toISOString().split("T")[0]);
  };

  const weekLabel = useCallback(() => {
    const start = new Date(weekStart);
    const end = new Date(weekStart);
    end.setDate(end.getDate() + 6);
    const fmt = (d) => `${d.getDate()}.${d.getMonth() + 1}`;
    return `${fmt(start)} - ${fmt(end)}`;
  }, [weekStart]);

  // Resolve recipes to add: in bulk mode use the array of ids, else just the
  // single recipe prop. Each entry is { id, title } so we can derive day-spread.
  const recipesToAdd = useMemo(() => {
    if (bulkRecipeIds && bulkRecipeIds.length > 0 && Array.isArray(allRecipes)) {
      return bulkRecipeIds
        .map((rid) => allRecipes.find((r) => r.id === rid))
        .filter(Boolean)
        .map((r) => ({ id: r.id, title: r.title }));
    }
    return recipe ? [{ id: recipe.id, title: recipe.title }] : [];
  }, [bulkRecipeIds, allRecipes, recipe]);

  const isBulk = recipesToAdd.length > 1;

  const handleAdd = async () => {
    if (recipesToAdd.length === 0) return;
    setSaving(true);
    try {
      const startIdx = DAYS.indexOf(day);
      // Spread the recipes across consecutive days starting from `day`.
      // Cap at 7 days — any extra recipes beyond the week are silently
      // dropped (we warn the user via toast).
      const slots = recipesToAdd.slice(0, 7 - startIdx).map((r, i) => ({
        ...r,
        targetDay: DAYS[startIdx + i],
      }));
      const skipped = recipesToAdd.length - slots.length;

      if (mealType === "baking") {
        // Baking plan: append one item per recipe, each on its own day.
        const existing = await api.getBakingPlan(weekStart).catch(() => ({ data: { week_start: weekStart, items: [] } }));
        const plan = { ...(existing.data || {}), week_start: weekStart, items: [...((existing.data && existing.data.items) || [])] };
        slots.forEach((slot) => {
          plan.items.push({
            id: (crypto && crypto.randomUUID) ? crypto.randomUUID() : `bk-${Date.now()}-${slot.id}`,
            recipe_id: slot.id,
            recipe_title: slot.title,
            custom_name: "",
            day: slot.targetDay,
            notes: "",
            category: bakingCategory || "Bakst",
          });
        });
        await api.updateBakingPlan(weekStart, plan);
        if (isBulk) {
          toast.success(`${slots.length} oppskrift${slots.length === 1 ? "" : "er"} lagt til i bakeplanen`);
        } else {
          toast.success(`Lagt til "${slots[0].title}" i bakeplanen (${DAY_LABELS[DAYS.indexOf(slots[0].targetDay)]})`);
        }
      } else {
        // Meal plan: one recipe per day, same meal type.
        const useShift = isHousehold ? "1" : shift;
        const existing = await api.getMealPlan(weekStart, useShift).catch(() => ({ data: { week_start: weekStart } }));
        const plan = { ...(existing.data || {}), week_start: weekStart };
        DAYS.forEach((d) => {
          if (!plan[d]) plan[d] = { breakfast: {}, lunch: {}, dinner: {}, dessert: {}, extras: {}, extra_labels: {} };
        });
        slots.forEach((slot) => {
          plan[slot.targetDay][mealType] = {
            recipe_id: slot.id,
            recipe_title: slot.title,
            custom_meal: "",
          };
        });
        await api.updateMealPlan(weekStart, plan, useShift);
        if (isBulk) {
          toast.success(`${slots.length} oppskrift${slots.length === 1 ? "" : "er"} lagt til som ${MEAL_LABELS[mealType]}`);
        } else {
          toast.success(`Lagt til "${slots[0].title}" som ${MEAL_LABELS[mealType]} på ${DAY_LABELS[DAYS.indexOf(slots[0].targetDay)]}`);
        }
      }

      if (skipped > 0) {
        toast.warning(`${skipped} oppskrift${skipped === 1 ? "" : "er"} fekk ikkje plass i veka — vel ein tidlegare startdag eller ei anna veke`);
      }

      if (onAfterSave) onAfterSave();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Feil ved lagring");
    } finally {
      setSaving(false);
    }
  };

  if (!recipe && recipesToAdd.length === 0) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="add-to-plan-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            <CalendarPlus className="h-5 w-5 text-navy-800" />
            {isBulk ? `Legg ${recipesToAdd.length} oppskrifter i ukeplan` : "Legg til i ukeplan"}
          </DialogTitle>
          <DialogDescription className="text-xs text-stone-500 truncate">
            {isBulk
              ? `Oppskriftene blir fordelt på påfølgjande dagar frå vald startdag`
              : (recipe?.title || "")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Bulk preview list */}
          {isBulk && (
            <div className="rounded-lg border border-stone-200 bg-stone-50/60 px-3 py-2 max-h-32 overflow-y-auto" data-testid="add-to-plan-bulk-list">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1.5">Valde oppskrifter ({recipesToAdd.length})</div>
              <ul className="space-y-1">
                {recipesToAdd.map((r, i) => (
                  <li key={r.id} className="text-xs text-stone-700 flex items-start gap-2">
                    <span className="text-stone-400 font-mono text-[10px] mt-0.5">{i + 1}.</span>
                    <span className="truncate flex-1">{r.title}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Meal type chips */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-stone-500 block mb-2">Type</label>
            <div className="flex flex-wrap gap-2" data-testid="add-to-plan-type-chips">
              {[
                { v: "breakfast", label: "Frokost", icon: ChefHat },
                { v: "lunch", label: "Lunsj", icon: ChefHat },
                { v: "dinner", label: "Middag", icon: ChefHat },
                { v: "dessert", label: "Dessert", icon: ChefHat },
                { v: "baking", label: "Bakst", icon: CakeSlice },
              ].map((opt) => {
                const Icon = opt.icon;
                const active = mealType === opt.v;
                return (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setMealType(opt.v)}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium border transition-all ${
                      active
                        ? "bg-navy-800 text-white border-navy-800"
                        : "bg-white text-stone-600 border-stone-200 hover:bg-stone-50"
                    }`}
                    data-testid={`add-to-plan-type-${opt.v}`}
                  >
                    <Icon className="h-3.5 w-3.5" /> {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Day */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-stone-500 block mb-1">{isBulk ? "Startdag" : "Dag"}</label>
            <Select value={day} onValueChange={setDay}>
              <SelectTrigger className="border-stone-200" data-testid="add-to-plan-day-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DAYS.map((d, i) => (
                  <SelectItem key={d} value={d}>{DAY_LABELS[i]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Week navigator */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-stone-500 block mb-1">Uke</label>
            <div className="flex items-center gap-2 bg-white border border-stone-200 rounded-lg px-2 py-1.5" data-testid="add-to-plan-week-nav">
              <button type="button" onClick={() => shiftWeek(-1)} className="p-1 hover:bg-stone-100 rounded-md transition-colors" data-testid="add-to-plan-prev-week">
                <ChevronLeft className="h-4 w-4 text-stone-500" />
              </button>
              <span className="text-sm font-medium text-stone-700 flex-1 text-center" data-testid="add-to-plan-week-label">
                {weekLabel()}
              </span>
              <button type="button" onClick={() => shiftWeek(1)} className="p-1 hover:bg-stone-100 rounded-md transition-colors" data-testid="add-to-plan-next-week">
                <ChevronRight className="h-4 w-4 text-stone-500" />
              </button>
            </div>
          </div>

          {/* Baking category */}
          {mealType === "baking" && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-stone-500 block mb-1">Bakst-kategori</label>
              <Select value={bakingCategory} onValueChange={setBakingCategory}>
                <SelectTrigger className="border-stone-200" data-testid="add-to-plan-baking-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {bakingCategories.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Shift selector (only for boat edition + non-baking) */}
          {!isHousehold && mealType !== "baking" && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-stone-500 block mb-1">Skift</label>
              <div className="flex gap-2" data-testid="add-to-plan-shift">
                {[
                  { v: "1", label: "Skift 1" },
                  { v: "2", label: "Skift 2" },
                ].map((s) => (
                  <button
                    key={s.v}
                    type="button"
                    onClick={() => setShift(s.v)}
                    className={`flex-1 rounded-md px-3 py-2 text-xs font-medium border transition-all ${
                      shift === s.v
                        ? "bg-navy-800 text-white border-navy-800"
                        : "bg-white text-stone-600 border-stone-200 hover:bg-stone-50"
                    }`}
                    data-testid={`add-to-plan-shift-${s.v}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={handleAdd}
            disabled={saving}
            className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-navy-800 px-4 py-2.5 text-sm font-medium text-white hover:bg-navy-900 transition-colors disabled:opacity-50"
            data-testid="add-to-plan-confirm-btn"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Lagrer...
              </>
            ) : (
              <>
                <Check className="h-4 w-4" /> {isBulk ? `Legg ${recipesToAdd.length} til i planen` : "Legg til i planen"}
              </>
            )}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
